<script setup lang="ts">
    // @ts-expect-error raison: lib externe mal typée
    import { defineProps, defineExpose, reactive, ref, watch, inject } from 'vue';
    import axios from 'axios';
    import { formatAmount } from '@/utils';

    defineExpose({ doGetDecaissementDetailsInfos });

    const BACKEND_URL = inject('BACKEND_URL');
    const props = defineProps<{
        startupInfos: any
    }>();
    const loadingUsers = ref(false);
    const errorMessageUsers = ref('');
    const formData = reactive({
        periode: null as any | null,
    });
    const periodes_options = ref([] as any[]);
    const collectivite = ref(null as any);
    const itemsList = ref([] as any[]);
    const itemsPaginated = ref([] as any[]);
    const queryParams = reactive({
        pageNumber: 1,
        sortDir: 'DESC',
        sortColumn: 'created_at',
        sortOption: 'created_at_desc',
    });
    const pagination = reactive({
        total: 0,
        per_page: 10,
        last_page: 1
    });
    const statsInfos = ref({
        montant_decaissement: 0,
        montant_prelevement: 0,
        solde: 0,
        montant_prelevement_expected: 0,
    });

    watch(() => props.startupInfos, (newVal: any) => {
        const isFirstChanged = !collectivite.value;
        collectivite.value = newVal.collectivite;
        periodes_options.value = newVal.periodes;
        formData.periode = newVal.periodes.find((p: any) => p.selected == true);
        if (!formData.periode) {
            formData.periode = newVal.periodes.length > 0 ? { value: newVal.periodes[0].value, label: newVal.periodes[0].label } : null;
        }
        if (isFirstChanged) {
            doGetDecaissementDetailsInfos();
        }
    }, { immediate: true });

    async function doGetDecaissementDetailsInfos() {
        try {
            loadingUsers.value = true;
            errorMessageUsers.value = '';
            const queryParamsRaw = {
                periode: formData.periode?.value || '',
            };
            const queryString = new URLSearchParams(Object.fromEntries(Object.entries(queryParamsRaw).map(([k,v]) => [k, String(v)]))).toString();
            const httpResponse = await axios.get(`/backoffice/finance/prelevements/get_decaissement_infos/${collectivite.value.id}?${queryString}`);

            statsInfos.value.montant_decaissement = httpResponse.data.montant_decaissement;
            statsInfos.value.montant_prelevement = httpResponse.data.montant_prelevement;
            statsInfos.value.solde = httpResponse.data.solde;

            itemsList.value = httpResponse.data.users;
            pagination.total = itemsList.value.length;
            pagination.last_page = Math.ceil(pagination.total / pagination.per_page);
            onPaginate(queryParams.pageNumber);
        } catch (error: any) {
            errorMessageUsers.value = error.message;
        } finally {
            loadingUsers.value = false;
        }
    }
    
    function onPaginate(pageNumber: number) {
        queryParams.pageNumber = pageNumber;
        const startIndex = (queryParams.pageNumber - 1) * pagination.per_page;
        const endIndex = startIndex + pagination.per_page;
        itemsPaginated.value = itemsList.value.slice(startIndex, endIndex);
    }

    function onSelectPeriode(periode: any) {
        formData.periode = periode;
        doGetDecaissementDetailsInfos();
    }
</script>
<template>
    <div class="s20 widget px-6 pt-8 pb-2">
        <div class="s10 mt--4 ml--4 mb-5 collapse-open active">
            <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                Prélèvements / Décaissements
            </span>
        </div>
        <div class="s10 mt--5-5 flex-right">
            <button class="btn lg py-3 black">
                <img src="/icons/icon-pdf.svg" class="btn-icon" alt="" />
                <span>Exporter en PDF</span>
            </button>
            <button class="btn lg py-3 black ">
                <img src="/icons/icon-excel.svg" class="btn-icon" alt="" />
                <span>Exporter en Excel</span>
            </button>
        </div>
        <div class="clear-both h-10"></div>

        <div class="s20 items-center gap-6 px-0 pb-6 collapse-content active">

            <div class="clear-both h-10"></div>

            <div class="s20 flex gap-3 mb-7">
                <span class="btn" @click="onSelectPeriode(periode)" :class="{'light': periode.value == formData.periode?.value, 'light2': periode.value != formData.periode?.value }" v-for="(periode, index) in periodes_options" :key="'periode_' + index">{{ periode.label }}</span>
            </div>

            <div class="flex items-center px-5 gap-6">
                <div class="s7">
                    <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                        <div class="circle-medium bg-pop-yellow"></div>
                        Montant prélevé 
                    </h5>
                    <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmount(statsInfos.montant_prelevement) }} F</h1>
                    <h5 class="secondary-font font-semibold text-grey-700 mb-4">0%</h5>
                    <!--Barre de progression - Component-->
                    <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                        <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-pop-yellow" style="width: 0%;">
                            0%
                        </div>
                    </div>
                    <!--End Barre de progression-->
                </div>
                <div class="s7">
                    <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                        <div class="circle-medium bg-color-1000"></div>
                        Montant décaissé 
                    </h5>
                    <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmount(statsInfos.montant_decaissement) }} F</h1>
                    <h5 class="secondary-font font-semibold text-grey-700 mb-4">0%</h5>
                    <!--Barre de progression - Component-->
                    <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                        <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-1000 text-white" style="width: 0%;">
                            0%
                        </div>
                    </div>
                    <!--End Barre de progression-->
                </div>
                <div class="s6">
                    <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                        <div class="circle-medium bg-green-light"></div>
                        Montant à prélevé
                    </h5>
                    <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmount(statsInfos.montant_prelevement_expected) }} F</h1>
                    <h5 class="secondary-font font-semibold text-grey-700 mb-4">0%</h5>
                    <!--Barre de progression - Component-->
                    <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                        <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-green-light" style="width: 0%;">
                            0%
                        </div>
                    </div>
                    <!--End Barre de progression-->
                </div>
            </div>

            <div class="clear-both h-20"></div>

            <!-- <div class="form-field-split my-4">
                <div class="form-field-floating reactive search sm">
                    <input type="text" class="form-input" name="date1" id="date1" placeholder="">
                    <label for="date1">Référence de transaction</label>
                </div>
            </div> -->

            <table class="table separator skin3 sm selection rounded"><!-- selection skin1 skin2 (bg1 bg2 bg3)  (skin3 rounded)  sm-->
                <thead>
                    <tr>
                        <th></th>
                        <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Information</th>
                        <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Collectivité</th>
                        <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Prélèvement</th>
                        <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Décaissement</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-if="loadingUsers">
                        <tr>
                            <td colspan="5" class="text-center py-5">
                                <div style="display: flex; justify-content: center" v-if="loadingUsers">
                                    <div class="loader dark"></div>
                                </div>
                            </td>
                        </tr>
                    </template>
                    <template v-else-if="itemsList.length === 0">
                        <tr>
                            <td colspan="5" class="text-center py-5">
                                Aucun membre trouvé.
                            </td>
                        </tr>
                    </template>
                    <template v-else>
                        <tr class="text-grey-700 over-effect" v-for="item in itemsPaginated" :key="item.id"><!-- over-effect active-->
                            <td class="flex align-items-center">
                                <div class="square-xl rounded-15" style="background-color: rgb(var(--color-warning-rgb), 0.2);">
                                    <img :src="!!item.photo_url ? item.photo_url : '/assets/users_avatar/600w/user.jpg'" width="100%" />
                                </div>
                                <div class="square-xl rounded-15 ml-3 px-3 py-3" style="background-color: rgb(var(--color-warning-rgb), 0.2);">
                                    <img src="/assets/logos/logo2.svg" width="100%" />
                                </div>
                            </td>
                            <td style="width: 20%;">
                                <p class="text-xl font-semibold mb-2">
                                    C788PMKJ &nbsp;  
                                    <span class="tag-state warning stroke">En cours</span>
                                    <span class="tag-state notif stroke">Actif</span>
                                    <br class="mb-3"/>
                                    {{ item.nom + ' ' + item.prenom }} 
                                </p>
                            </td>
                            <td style="width: 15%;" class="text-xl font-semibold mb-2">
                                {{ item.collectivite_nom }}
                            </td>
                            <td>
                                <!--Prélèvement - Component-->
                                <div class="widget s20 border border-3 active shadow-200 rounded-30 gap-5 px-7 pt-4 pb-2 scale-0-8-5" v-if="item.prelevement_id">
                                    <div class="s10">
                                        <h6 class="secondary-font font-semibold mb-1">{{ item.prelevement_periode_prelevement_formatted }}</h6>
                                        <h3 class="secondary-font font-semibold text-grey-900 py-1">{{ formatAmount(item.prelevement_montant_prelevement) }} F</h3>
                                    </div>
                                    <div class="s10 text-right">
                                        <h5 class="secondary-font font-semibold text-grey-600 pb-2 display-none">1m 29j 24min</h5>
                                        <div class="mb-4">
                                            <span class="tag-state notif stroke">{{ item.status_prelevement_label }}</span>
                                        </div>
                                        <h6 class="secondary-font font-semibold text-grey-600 pt-1 mb-4">Le {{ item.prelevement_created_at_formatted }}</h6>
                                        <button class="btn-ultra w-100 shadow-0 display-none">
                                            <div class="btn-show">
                                                <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                                                <span>Valider</span>
                                            </div>
                                            <div class="btn-loading">
                                                <div class="loader-dots"></div>
                                            </div>
                                        </button>
                                    </div>
                                </div>
                                <div class="widget s20 border border-3 shadow-200 rounded-30 gap-5 px-7 pt-4 pb-2 scale-0-8-5" v-if="!item.prelevement_id">
                                    <div class="s20 text-center">
                                        Aucun prélèvement
                                    </div>
                                </div>
                                <!--End Prélèvement-->
                            </td>
                            <td>
                                <!--Décaissement - Component-->
                                <div class="widget s20 border border-3 shadow-200 rounded-30 gap-5 px-7 pt-4 pb-2 scale-0-8-5" v-if="item.souscription_id" :class="{'active': item.souscription_statut == 'done' }">
                                    <div class="s10">
                                        <h6 class="secondary-font font-semibold mb-1">{{ item.souscription_date_prevision_formatted }}</h6>
                                        <h3 class="secondary-font font-semibold text-grey-900 py-1">{{ formatAmount(item.souscription_pouvoir_achat) }} F</h3>
                                        <p class="secondary-font font-semibold text-purple display-none">1.5%</p>
                                    </div>
                                    <div class="s10 text-right">
                                        <h5 class="secondary-font font-semibold text-primary-color pb-1 display-none">1m 29j 24min</h5>
                                        <div class="mb-4">
                                            <span class="tag-state danger stroke" v-if="item.souscription_statut == 'pending'">En attente</span>
                                            <span class="tag-state success stroke" v-if="item.souscription_statut == 'done'">Effectué</span>
                                        </div>
                                        <p class="secondary-font font-semibold text-grey-600 pt-1 display-none">Date de déblocage</p>
                                        <h6 class="secondary-font font-semibold text-grey-600 py-1">{{ item.souscription_created_at_formatted }}</h6>
                                    </div>
                                    <div class="s20 mt-2 display-none">
                                        <p class="s10 font-medium text-sm text-grey-600 mt-2">
                                            Prélèvement <span class="text-sm color-success">(Effectué)</span>
                                        </p>
                                        <div class="s10">
                                            <button class="btn-ultra w-100">
                                                <div class="btn-show">
                                                    <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                                                    <span>Décaisser</span>
                                                </div>
                                                <div class="btn-loading">
                                                    <div class="loader-dots"></div>
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div class="widget s20 border border-3 shadow-200 rounded-30 gap-5 px-7 pt-4 pb-2 scale-0-8-5" v-if="!item.souscription_id">
                                    <div class="s20 text-center">
                                        Aucun décaissement
                                    </div>
                                </div>
                                <!--End Décaissement-->
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
            <!--Pagination-->
            <div style="display: flex; flex-direction: row; justify-content: center;">
                <custom-pagination v-model="queryParams.pageNumber" :total="pagination.total" :per-page="pagination.per_page" :last_page="pagination.last_page" @update:modelValue="onPaginate" />
            </div>
            <!---->
        </div>
    </div>
</template>